import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import AgenticCommerceEmptyState, { EmptyStateVariant } from './agentic_commerce_empty_state';
import connect from 'components/lib/connect';
import EmbeddedReport, { ReportStatus } from 'components/reporting/report/embedded_report';
import { findAgenticCommerceReportConfig } from 'models/reports/report_configs';
import PageLayout from 'components/page_layout';
import { SIDEBAR_AUTO_COLLAPSE_WIDTH } from 'components/sidebar/sidebar';
import SpinnerTwo from 'components/common/spinner_two';
import StackContainer from 'components/common/containers/stack_container';
import useWindowSize from 'components/hooks/use_window_size';

export function AgenticCommercePage({
  isCommerceEnabled,
  isReportAvailable,
  isReportConfigLoading,
  shouldCollapseSidebar,
}) {
  const { windowWidth } = useWindowSize();
  const isSidebarCollapsed = shouldCollapseSidebar || windowWidth <= SIDEBAR_AUTO_COLLAPSE_WIDTH;
  const isEmptyState = !isCommerceEnabled || (!isReportConfigLoading && !isReportAvailable);

  return (
    <StyledPageLayout $isFullHeight={!isEmptyState} className="agenticCommerce" id="agentic-commerce">
      {renderContent()}
    </StyledPageLayout>
  );

  function renderContent() {
    if (!isCommerceEnabled) {
      return <AgenticCommerceEmptyState isSidebarCollapsed={isSidebarCollapsed} variant={EmptyStateVariant.UPSELL} />;
    }
    if (isReportConfigLoading) {
      return <ReportSpinner />;
    }
    if (!isReportAvailable) {
      return (
        <AgenticCommerceEmptyState isSidebarCollapsed={isSidebarCollapsed} variant={EmptyStateVariant.ON_ITS_WAY} />
      );
    }
    return <AgenticCommerceReport />;
  }
}

AgenticCommercePage.propTypes = {
  isCommerceEnabled: PropTypes.bool.isRequired,
  isReportAvailable: PropTypes.bool.isRequired,
  isReportConfigLoading: PropTypes.bool.isRequired,
  shouldCollapseSidebar: PropTypes.bool.isRequired,
};

function AgenticCommerceReport() {
  const [status, setStatus] = useState(ReportStatus.LOADING);
  const isLoading = status === ReportStatus.LOADING;

  return (
    <React.Fragment>
      {isLoading && <ReportSpinner />}
      <ReportWrapper $isHidden={isLoading} data-aid="agenticCommerce-report">
        <EmbeddedReport onStatusChange={setStatus} />
      </ReportWrapper>
    </React.Fragment>
  );
}

function ReportSpinner() {
  const { t } = useTranslation();

  return (
    <SpinnerContainer data-aid="agenticCommerce-spinner">
      <StackContainer stack="small">
        <StyledSpinner />
        {t('Loading your report')}
      </StackContainer>
    </SpinnerContainer>
  );
}

// The report iframe and its spinner size to 100% height, so the page container needs a height too, as on the
// Reports page. The empty states keep their natural height.
const StyledPageLayout = styled(PageLayout)`
  ${p =>
    p.$isFullHeight &&
    css`
      .page-childrenContainer {
        height: 100%;
      }
    `}
`;

// An inline iframe sits on the text baseline, which makes the line taller than the iframe and the page scroll.
const ReportWrapper = styled.div`
  display: ${p => (p.$isHidden ? 'none' : 'block')};
  height: 100%;

  iframe {
    display: block;
  }
`;

const SpinnerContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
`;

const StyledSpinner = styled(SpinnerTwo).attrs(p => ({ color: p.theme.colors.green400, size: 120, stroke: 3 }))``;

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const reportConfigsProvider = getProvider('reportConfigs');

  return {
    isCommerceEnabled: isFeatureEnabled('enableBeacon'),
    isReportAvailable: !!findAgenticCommerceReportConfig(reportConfigsProvider.get().configs),
    isReportConfigLoading: reportConfigsProvider.isLoading(),
    shouldCollapseSidebar: getProvider('agentLocalState').get().shouldCollapseSidebar,
  };
}

export default connect(mapStateToProps)(AgenticCommercePage);
